import React, { useEffect, useRef, useMemo } from 'react';
import {
  MapContainer, TileLayer, Polyline, Marker, Popup, Tooltip,
  CircleMarker, useMap, LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './FlatMapComponent.css';
import { unwrapLng, unwrapLngs, splitAtAntimeridian } from '../utils/geo';
import { LIGHT_BASE, DARK_BASE } from '../utils/basemaps';

const NAV_STATUS_COLORS = {
  0: '#28a745', 1: '#ffc107', 2: '#dc2626', 3: '#fd7e14', 4: '#fd7e14',
  5: '#4a6b25', 6: '#dc2626', 7: '#6f42c1', 8: '#28a745',
};

const NAV_STATUS_LABELS = {
  0: 'Under way', 1: 'At anchor', 2: 'Not under command', 3: 'Restricted',
  4: 'Constrained by draught', 5: 'Moored', 6: 'Aground', 7: 'Fishing',
  8: 'Sailing', 14: 'AIS-SART', 15: 'Undefined',
};

const DEFAULT_CENTER = [20, 0];
const DEFAULT_ZOOM = 2;

const projectPoint = (lat, lng, bearingDeg, distanceNm) => {
  const R = 3440.065;
  const d = distanceNm / R;
  const brng = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lng * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng)
  );
  const lon2 = lon1 + Math.atan2(
    Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI];
};

const buildVesselIcon = (heading, navStatusCode) => {
  const color = NAV_STATUS_COLORS[navStatusCode] || '#4a6b25';
  const rotation = heading || 0;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-12 -14 24 28" width="28" height="32">
      <g transform="rotate(${rotation} 0 0)">
        <polygon points="0,-12 8,10 0,5 -8,10" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      </g>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'flat-map-vessel-icon',
    iconSize: [28, 32],
    iconAnchor: [14, 16],
  });
};

const buildPortIcon = (portKind) => {
  const color = portKind === 'loading' ? '#28a745' : portKind === 'discharge' ? '#dc2626' : '#4a6b25';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="8" fill="none" stroke="${color}" stroke-width="2"/>
      <circle cx="9" cy="9" r="3" fill="${color}"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'flat-map-port-icon',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
};

const generateGreatCirclePoints = (startLat, startLng, endLat, endLng, numPoints = 60) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  let sLng = startLng;
  let eLng = endLng;
  let lngDiff = eLng - sLng;
  if (lngDiff > 180) eLng -= 360;
  else if (lngDiff < -180) eLng += 360;

  const lat1 = toRad(startLat);
  const lon1 = toRad(sLng);
  const lat2 = toRad(endLat);
  const lon2 = toRad(eLng);

  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2
    )
  );

  if (d < 1e-10) return [[[startLat, startLng], [endLat, endLng]]];

  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    const f = i / numPoints;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
    const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    let lat = toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)));
    let lng = toDeg(Math.atan2(y, x));
    if (lng > 180) lng -= 360;
    else if (lng < -180) lng += 360;
    points.push([lat, lng]);
  }

  return splitAtAntimeridian(points);
};

/**
 * Fits the map bounds to encompass vessel and port markers.
 * Only triggers on structural changes, not on minor vessel position updates.
 * Port + sailed longitudes are unwrapped into the voyage frame so the bounds
 * span the SHORT way (across the voyage) rather than the long way through the
 * far side of the globe — critical for date-line-crossing voyages.
 */
const FitBounds = ({ vesselData, portMarkers, sailedWaypoints, frameReferenceLng }) => {
  const map = useMap();
  const lastBoundsKey = useRef('');

  const boundsKey = useMemo(() => {
    const parts = [];
    if (vesselData?.latitude != null) parts.push('v');
    portMarkers.forEach((pm, i) => {
      if (pm.lat != null) parts.push(`p${i}:${pm.lat},${pm.lon}`);
    });
    // Re-fit once the sailed line arrives — it follows shipping lanes and can
    // bow outside the straight loading->vessel span.
    if (sailedWaypoints && sailedWaypoints.length > 1) parts.push(`s${sailedWaypoints.length}`);
    return parts.join('|');
  }, [vesselData, portMarkers, sailedWaypoints]);

  useEffect(() => {
    if (!boundsKey || boundsKey === lastBoundsKey.current) return;
    lastBoundsKey.current = boundsKey;

    const allPoints = [];

    if (vesselData?.latitude != null && vesselData?.longitude != null) {
      allPoints.push([vesselData.latitude, vesselData.longitude]);
    }

    portMarkers.forEach((pm) => {
      if (pm.lat != null && pm.lon != null) allPoints.push([pm.lat, unwrapLng(frameReferenceLng, pm.lon)]);
    });

    (sailedWaypoints || []).forEach((p) => {
      if (Array.isArray(p) && p[0] != null && p[1] != null) allPoints.push([p[0], unwrapLng(frameReferenceLng, p[1])]);
    });

    if (allPoints.length === 0) return;

    if (allPoints.length === 1) {
      map.flyTo(allPoints[0], 6, { duration: 1.4 });
      return;
    }

    const bounds = L.latLngBounds(allPoints);
    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 10, duration: 1.4 });
  }, [map, boundsKey, vesselData, portMarkers, sailedWaypoints, frameReferenceLng]);

  return null;
};

const FlatMapComponent = ({
  vesselData,
  portMarkers = [],
  // Optional navigable NavAPI sea routes ([lat, lon] waypoints). When supplied
  // they are drawn as a single continuous unwrapped line and take precedence
  // over the internally-generated great-circle segments below. Left empty by
  // callers (e.g. the delivery form) that still rely on the great-circle path.
  routeWaypoints = [],
  sailedWaypoints = [],
}) => {
  const mapRef = useRef(null);

  // This mini-map shows ONE voyage on a wrapping/infinitely-scrollable map, so
  // date-line-crossing geometry is rendered in a single continuous "voyage
  // frame" (longitude unwrapping) rather than split 360 deg apart. Frame
  // reference: the live vessel longitude when present, else the first available
  // port marker longitude, else 0.
  const frameReferenceLng = useMemo(() => {
    if (vesselData?.longitude != null) return vesselData.longitude;
    const firstPort = portMarkers.find(pm => pm.lon != null);
    if (firstPort) return firstPort.lon;
    return 0;
  }, [vesselData, portMarkers]);

  // Navigable port-to-port sea route (loading -> discharge) from NavAPI Sea
  // Routing, unwrapped into the voyage frame as one continuous line. [lat, lon].
  const routeLine = useMemo(() => {
    const pts = (routeWaypoints || []).filter(p => Array.isArray(p) && p[0] != null && p[1] != null);
    if (pts.length < 2) return null;
    return unwrapLngs(frameReferenceLng, pts);
  }, [routeWaypoints, frameReferenceLng]);

  // Estimated sailed route (loading port -> live position) from NavAPI, unwrapped
  // into the SAME voyage frame so the two lines join at the vessel marker.
  const sailedLine = useMemo(() => {
    const pts = (sailedWaypoints || []).filter(p => Array.isArray(p) && p[0] != null && p[1] != null);
    if (pts.length < 2) return null;
    return unwrapLngs(frameReferenceLng, pts);
  }, [sailedWaypoints, frameReferenceLng]);

  const hasNavRoute = Boolean(routeLine || sailedLine);

  // Great-circle fallback route segments (each avoids the antimeridian wrap).
  // Used only when no navigable NavAPI route was supplied for this voyage.
  const vesselToDestSegments = useMemo(() => {
    if (hasNavRoute) return [];
    if (!vesselData?.latitude || !vesselData?.longitude) return [];
    const discharge = portMarkers.find(p => p.type === 'discharge');
    if (!discharge?.lat || !discharge?.lon) return [];
    return generateGreatCirclePoints(
      vesselData.latitude, vesselData.longitude,
      discharge.lat, discharge.lon
    );
  }, [hasNavRoute, vesselData, portMarkers]);

  const originToVesselSegments = useMemo(() => {
    if (hasNavRoute) return [];
    if (!vesselData?.latitude || !vesselData?.longitude) return [];
    const loading = portMarkers.find(p => p.type === 'loading');
    if (!loading?.lat || !loading?.lon) return [];
    return generateGreatCirclePoints(
      loading.lat, loading.lon,
      vesselData.latitude, vesselData.longitude
    );
  }, [hasNavRoute, vesselData, portMarkers]);

  const courseProjection = useMemo(() => {
    if (!vesselData?.latitude || !vesselData?.longitude || vesselData.course == null) return null;
    const speed = vesselData.speed || 0;
    if (speed < 0.5) return null;
    const hoursAhead = 3;
    const distanceNm = speed * hoursAhead;
    const endPoint = projectPoint(vesselData.latitude, vesselData.longitude, vesselData.course, distanceNm);
    return [[vesselData.latitude, vesselData.longitude], endPoint];
  }, [vesselData]);

  const vesselIcon = useMemo(() => {
    if (!vesselData) return null;
    const heading = vesselData.heading || vesselData.course || 0;
    return buildVesselIcon(heading, vesselData.nav_status_code);
  }, [vesselData]);

  const navStatusLabel = NAV_STATUS_LABELS[vesselData?.nav_status_code] || 'Unknown';
  const hasVessel = vesselData?.latitude != null && vesselData?.longitude != null;
  const isEmpty = !vesselData && portMarkers.length === 0;

  return (
    <div className="flat-map-container">
      {isEmpty && (
        <div className="flat-map-message">
          <h3>No Vessel Selected</h3>
          <p>Search and select a vessel above to view live tracking on the map.</p>
        </div>
      )}

      <MapContainer
        ref={mapRef}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        className="flat-map"
        scrollWheelZoom={true}
      >
        <LayersControl position="topright">
          {/* Default base is Esri World Street Map: it renders English
              transliterations alongside local scripts at street zooms (CARTO
              rasters are local-language only, e.g. Japanese-only in Japan). */}
          <LayersControl.BaseLayer checked name="Streets (English)">
            <TileLayer
              attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, OpenStreetMap contributors'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={LIGHT_BASE.name}>
            <TileLayer attribution={LIGHT_BASE.attribution} url={LIGHT_BASE.url} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={DARK_BASE.name}>
            <TileLayer attribution={DARK_BASE.attribution} url={DARK_BASE.url} />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <FitBounds vesselData={vesselData} portMarkers={portMarkers} sailedWaypoints={sailedWaypoints} frameReferenceLng={frameReferenceLng} />

        {/* Navigable estimated sailed route (loading port -> live position),
            unwrapped into the voyage frame. Drawn first (behind the planned
            line) and muted so it reads as the ship's already-covered distance. */}
        {sailedLine && (
          <Polyline
            positions={sailedLine}
            pathOptions={{ color: '#4a6b25', weight: 3, opacity: 0.35 }}
          >
            <Tooltip direction="center" opacity={0.9} permanent={false} sticky>
              <span className="flat-map-track-tooltip">Estimated sailed route from loading port</span>
            </Tooltip>
          </Polyline>
        )}

        {/* Navigable sea route (loading port -> discharge port), unwrapped into
            the voyage frame as one continuous line. */}
        {routeLine && (
          <Polyline
            positions={routeLine}
            pathOptions={{ color: '#4a6b25', weight: 3, opacity: 0.75 }}
          >
            <Tooltip direction="center" opacity={0.9} permanent={false} sticky>
              <span className="flat-map-track-tooltip">Planned sea route to discharge port</span>
            </Tooltip>
          </Polyline>
        )}

        {/* Great-circle fallback: route from loading port to vessel (already
            traveled). Only rendered when no navigable NavAPI route was supplied. */}
        {originToVesselSegments.length > 0 && originToVesselSegments.map((seg, i) => (
          <Polyline
            key={`origin-seg-${i}`}
            positions={seg}
            pathOptions={{ color: '#6c757d', weight: 2, opacity: 0.4, dashArray: '6 8' }}
          >
            {i === 0 && (
              <Tooltip direction="center" opacity={0.9} permanent={false} sticky>
                <span className="flat-map-track-tooltip">Route from loading port</span>
              </Tooltip>
            )}
          </Polyline>
        ))}

        {/* Great-circle fallback: route from vessel to discharge port
            (remaining voyage). */}
        {vesselToDestSegments.length > 0 && vesselToDestSegments.map((seg, i) => (
          <Polyline
            key={`dest-seg-${i}`}
            positions={seg}
            pathOptions={{ color: '#4a6b25', weight: 2.5, opacity: 0.6, dashArray: '10 6' }}
          >
            {i === 0 && (
              <Tooltip direction="center" opacity={0.9} permanent={false} sticky>
                <span className="flat-map-track-tooltip">Projected route to discharge port</span>
              </Tooltip>
            )}
          </Polyline>
        ))}

        {/* Course projection line (short-term heading) */}
        {courseProjection && (
          <Polyline
            positions={courseProjection}
            pathOptions={{ color: '#ff9800', weight: 2, opacity: 0.8, dashArray: '6 4' }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={0.9} permanent={false}>
              <span>Projected course ({vesselData.course?.toFixed(0)}&deg;) at {vesselData.speed?.toFixed(1)} kn</span>
            </Tooltip>
          </Polyline>
        )}

        {/* Course projection endpoint */}
        {courseProjection && (
          <CircleMarker
            center={courseProjection[1]}
            radius={4}
            pathOptions={{ color: '#ff9800', fillColor: '#ff9800', fillOpacity: 0.6, weight: 1 }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={0.9}>
              <span>~3h projected position</span>
            </Tooltip>
          </CircleMarker>
        )}

        {/* Vessel marker */}
        {hasVessel && (
          <Marker
            position={[vesselData.latitude, vesselData.longitude]}
            icon={vesselIcon}
          >
            <Tooltip direction="right" offset={[18, 0]} opacity={0.95} permanent>
              <div className="flat-map-speed-label">
                {vesselData.speed != null ? `${vesselData.speed.toFixed(1)} kn` : '--'}
                {vesselData.heading != null && <span className="flat-map-heading-label">{vesselData.heading.toFixed(0)}&deg;</span>}
              </div>
            </Tooltip>
            <Popup>
              <div className="flat-map-popup flat-map-popup-rich">
                <div className="flat-map-popup-header">
                  <strong>{vesselData.ship_name || 'Unknown Vessel'}</strong>
                  {vesselData.nav_status_code != null && (
                    <span className="flat-map-popup-badge" style={{ backgroundColor: NAV_STATUS_COLORS[vesselData.nav_status_code] || '#6c757d' }}>
                      {navStatusLabel}
                    </span>
                  )}
                </div>
                <div className="flat-map-popup-grid">
                  {vesselData.imo && <div className="flat-map-popup-field"><span className="flat-map-popup-label">IMO</span><span>{vesselData.imo}</span></div>}
                  {vesselData.mmsi && <div className="flat-map-popup-field"><span className="flat-map-popup-label">MMSI</span><span>{vesselData.mmsi}</span></div>}
                  {vesselData.speed != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Speed</span><span>{vesselData.speed.toFixed(1)} kn</span></div>}
                  {vesselData.course != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Course</span><span>{vesselData.course.toFixed(1)}&deg;</span></div>}
                  {vesselData.heading != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Heading</span><span>{vesselData.heading.toFixed(0)}&deg;</span></div>}
                  {vesselData.draught != null && <div className="flat-map-popup-field"><span className="flat-map-popup-label">Draught</span><span>{vesselData.draught.toFixed(1)} m</span></div>}
                </div>
                {(vesselData.origin || vesselData.destination) && (
                  <div className="flat-map-popup-voyage">
                    {vesselData.origin && (() => {
                      const resolved = portMarkers.find(pm => pm.type === 'loading');
                      return <span>From: {resolved ? `${resolved.name}, ${resolved.country}` : vesselData.origin}</span>;
                    })()}
                    {vesselData.destination && (() => {
                      const resolved = portMarkers.find(pm => pm.type === 'discharge');
                      return <span>To: {resolved ? `${resolved.name}, ${resolved.country}` : vesselData.destination}</span>;
                    })()}
                  </div>
                )}
                {vesselData.position_updated && (
                  <div className="flat-map-popup-updated">
                    Updated: {new Date(vesselData.position_updated).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Port markers — longitude unwrapped into the voyage frame so a port
            on the far side of the dateline renders next to the vessel/route. */}
        {portMarkers.map((pm, idx) => (
          pm.lat != null && pm.lon != null && (
            <Marker key={`port-${idx}`} position={[pm.lat, unwrapLng(frameReferenceLng, pm.lon)]} icon={buildPortIcon(pm.type)}>
              <Popup>
                <div className="flat-map-popup">
                  <strong>{pm.name || 'Port'}</strong>
                  <span>{pm.type === 'loading' ? 'Loading Port' : pm.type === 'discharge' ? 'Discharge Port' : 'Port'}</span>
                </div>
              </Popup>
              <Tooltip direction="bottom" offset={[0, 8]} opacity={0.9} permanent>
                <span className="flat-map-port-label">
                  {pm.type === 'loading' ? 'Load' : pm.type === 'discharge' ? 'Disch' : 'Port'}: {pm.name || ''}
                </span>
              </Tooltip>
            </Marker>
          )
        ))}
      </MapContainer>
    </div>
  );
};

export default FlatMapComponent;
